import { Popover, Portal, DataList, FormatByte, Box, Center } from '@chakra-ui/react';
import { MyTrans } from '@halo/ui-react';
import { MediaSDKMediaType } from '@halo/chat-sdk';

const names: any = {
  inboundBytes: {
    text: <MyTrans i18nKey="calls.download" />,
    unit: 'bit',
  },
  outboundBytes: {
    text: <MyTrans i18nKey="calls.upload" />,
    unit: 'bit',
  },
  quality: {
    text: <MyTrans i18nKey="calls.networkQuality" />,
    unit: '',
  },
  averageQuality: {
    text: <MyTrans i18nKey="calls.averageNetworkQuality" />,
    unit: '',
  },
  jitter: {
    text: <MyTrans i18nKey="calls.networkJitter" />,
    unit: 'ms',
  },
  roundTripTime: {
    text: <MyTrans i18nKey="calls.roundTripTime" />,
    unit: 'ms',
  },
  lostRate: {
    text: <MyTrans i18nKey="calls.lostRate" />,
    unit: '',
  },
  frameWidth: {
    text: <MyTrans i18nKey="calls.frameWidth" />,
    unit: 'px',
  },
  frameHeight: {
    text: <MyTrans i18nKey="calls.frameHeight" />,
    unit: 'px',
  },
  framesPerSecond: {
    text: <MyTrans i18nKey="calls.framesPerSecond" />,
    unit: 'fps',
  },
  velocityPull: {
    text: <MyTrans i18nKey="calls.velocityPull" />,
    unit: 'B/S',
  },
  velocityPush: {
    text: <MyTrans i18nKey="calls.velocityPush" />,
    unit: 'B/S',
  },
};

const typeNames: any = {
  [MediaSDKMediaType.MICROPHONECAMERA_AUDIO]: <MyTrans i18nKey="calls.audioName" />,
  [MediaSDKMediaType.MICROPHONECAMERA_VIDEO]: <MyTrans i18nKey="calls.videoName" />,
  [MediaSDKMediaType.SCREENSHARING_AUDIO]: <MyTrans i18nKey="calls.screenAudioName" />,
  [MediaSDKMediaType.SCREENSHARING_VIDEO]: <MyTrans i18nKey="calls.screenVideoName" />,
};

export function UserRoomInfo({
  open,
  member,
  reports,
  roomNetwork,
  onClose,
}: {
  open: boolean;

  reports?: Record<string, any>[];
  roomNetwork?: Record<string, any>;
  member: any;
  onClose: () => void;
}) {
  const videoReport = useMemo(() => {
    if (member?.videoMid) {
      return reports?.find((item) => item.mid == member.videoMid);
    }
    return {};
  }, [member?.videoMid, reports]);

  const screenReport = useMemo(() => {
    if (member?.screenMid) {
      return reports?.find((item) => item.mid == member.screenMid);
    }
    return {};
  }, [member?.screenMid, reports]);

  const sends = useMemo(() => {
    return Object.keys(roomNetwork?.details?.[member?.user_id]?.sends || {}).map((key) => {
      let stat = {
        ...roomNetwork?.details?.[member?.user_id]?.sends?.[key],
      };
      if (
        [
          MediaSDKMediaType.MICROPHONECAMERA_VIDEO,
          MediaSDKMediaType.SCREENSHARING_VIDEO,
        ].includes(key as any)
      ) {
        stat = {
          ...stat,
          frameWidth:
            (member?.screenSharing ? screenReport?.frameWidth : videoReport?.frameWidth) || 0,
          frameHeight:
            (member?.screenSharing ? screenReport?.frameHeight : videoReport?.frameHeight) || 0,
          framesPerSecond:
            (member?.screenSharing
              ? screenReport?.framesPerSecond
              : videoReport?.framesPerSecond) || 0,
        };
      }
      const stats = Object.entries(stat)?.map(([key, value]: [string, any]) => {
        const { text, unit } = names[key] ?? {};
        return {
          id: key,
          label: text,
          value: ['inboundBytes', 'outboundBytes'].includes(key) ? (
            <FormatByte value={value} unit={unit} />
          ) : (
            `${value}${unit}`
          ),
        };
      });
      return {
        id: key,
        label: typeNames[key as any],
        stats,
      };
    });
  }, [roomNetwork, videoReport, typeNames]);

  return (
    <Popover.Root open={open} onInteractOutside={onClose} unmountOnExit>
      <Portal>
        <Popover.Positioner>
          <Popover.Content bg="blackAlpha.70">
            <Popover.Body>
              <DataList.Root orientation="horizontal" h="600px" overflow="auto">
                {sends.map((send) => {
                  return (
                    <Box key={send.id}>
                      <Center p={2} color="whiteAlpha.80">
                        {send.label}
                      </Center>
                      {send.stats.map((item) => (
                        <DataList.Item key={item.id}>
                          <DataList.ItemLabel>{item.label}</DataList.ItemLabel>
                          <DataList.ItemValue color="whiteAlpha.80">
                            {item.value}
                          </DataList.ItemValue>
                        </DataList.Item>
                      ))}
                    </Box>
                  );
                })}
              </DataList.Root>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
